/**
 * AdminLayout - Shared layout wrapper for admin sub-pages
 * Provides tab navigation between Dashboard, Users, Billing, Usage.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';

const PageContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 48px 24px 80px;

  @media (max-width: 640px) {
    padding: 24px 16px 64px;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 8px 0;
  font-family: ${(p) => p.theme.fonts?.mono};

  @media (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    font-size: 1.3rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0 0 24px 0;
`;

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 32px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  overflow-x: auto;
`;

const Tab = styled.button`
  padding: 10px 20px;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  background: transparent;
  color: ${(p) => p.$active ? p.theme.colors.text : p.theme.colors.textMuted};
  cursor: pointer;
  border-bottom: 2px solid ${(p) => p.$active ? p.theme.colors.primary : 'transparent'};
  transition: all 0.2s;
  white-space: nowrap;
  font-family: ${(p) => p.theme.fonts.main};
  margin-bottom: -1px;

  &:hover {
    color: ${(p) => p.theme.colors.text};
    background: ${(p) => p.theme.colors.bgTertiary};
  }

  @media (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    padding: 8px 14px;
    font-size: 0.82rem;
  }
`;

const tabs = [
  { key: 'dashboard', path: '/admin', labelJa: 'ダッシュボード', labelEn: 'Dashboard' },
  { key: 'users', path: '/admin/users', labelJa: 'ユーザー管理', labelEn: 'Users' },
  { key: 'usage', path: '/admin/usage', labelJa: 'AI利用', labelEn: 'AI Usage' },
  { key: 'billing', path: '/admin/billing', labelJa: '企業 (B2B)', labelEn: 'Companies (B2B)' },
];

function AdminLayout({ active, children }) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <PageContainer>
      <PageTitle>STRUCT CODE Admin</PageTitle>
      <PageSubtitle>{t('サービス運営管理画面', 'Service Operations Dashboard')}</PageSubtitle>

      <TabRow>
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            $active={active === tab.key}
            onClick={() => navigate(tab.path)}
          >
            {t(tab.labelJa, tab.labelEn)}
          </Tab>
        ))}
      </TabRow>

      {children}
    </PageContainer>
  );
}

export default AdminLayout;
